/**
 * Rein clientseitiger Speicher fuer Watchlist + Depot ohne Login (Feedback-Runde 2,
 * Punkt 5: "Watchlist + Depot ohne Login nutzbar machen"). Ohne Session gibt es keine
 * user_id fuer die RLS-Policies auf watchlists/portfolios/portfolio_positions - Speicherung
 * laeuft deshalb rein lokal (localStorage), analog zum bestehenden "Zuletzt angesehen"-
 * Muster (recent-stocks.tsx, Key ek_searched_symbols). Kein geraeteuebergreifender Zugriff,
 * bis sich der Gast registriert - siehe migrateGuestDataToAccount() fuer den Uebergang.
 */

const WATCHLIST_KEY = "ek_watchlist_v1";
const PORTFOLIO_KEY = "ek_portfolio_v1";
/** Watchlist-Gruppen (backlog.md -> "Weiterer Nutzwert-Katalog" -> Watchlist) - bewusst
 * ein eigener, separater Key statt den bestehenden WATCHLIST_KEY (string[]) umzubauen:
 * keine Migration alter, bereits gespeicherter Daten noetig, bestehende Konsumenten von
 * getLocalWatchlist() bleiben komplett unveraendert. Symbol -> Gruppenname, ein Symbol
 * ohne Eintrag hier gilt als "ohne Gruppe" (Default-Verhalten). */
const WATCHLIST_GROUPS_KEY = "ek_watchlist_groups_v1";

export interface LocalPortfolioPosition {
  id: string;
  symbol: string;
  quantity: number;
  purchasePrice: number;
  targetWeight: number;
}

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new Event(`${key}-changed`));
}

export function getLocalWatchlist(): string[] {
  return readJson(WATCHLIST_KEY, []);
}

export function isInLocalWatchlist(symbol: string): boolean {
  return getLocalWatchlist().includes(symbol);
}

export function removeLocalWatchlistSymbol(symbol: string): void {
  writeJson(WATCHLIST_KEY, getLocalWatchlist().filter((s) => s !== symbol));
  // Verwaiste Gruppen-Zuordnung mit entfernen, sonst haeuft sich ueber die Zeit toter
  // localStorage-Ballast an, der nie wieder sichtbar wird.
  if (symbol in getLocalWatchlistGroups()) setLocalWatchlistGroup(symbol, null);
}

/** Symbol -> Gruppenname, siehe WATCHLIST_GROUPS_KEY-Docstring oben. */
export function getLocalWatchlistGroups(): Record<string, string> {
  return readJson(WATCHLIST_GROUPS_KEY, {});
}

/** `group` null oder leer entfernt die Zuordnung (Symbol gilt wieder als "ohne Gruppe"). */
export function setLocalWatchlistGroup(symbol: string, group: string | null): void {
  const groups = getLocalWatchlistGroups();
  const trimmed = group?.trim();
  if (!trimmed) {
    delete groups[symbol];
  } else {
    groups[symbol] = trimmed;
  }
  writeJson(WATCHLIST_GROUPS_KEY, groups);
}

export function getLocalPortfolio(): LocalPortfolioPosition[] {
  return readJson(PORTFOLIO_KEY, []);
}

export function removeLocalPortfolioPosition(id: string): void {
  writeJson(PORTFOLIO_KEY, getLocalPortfolio().filter((p) => p.id !== id));
}

export function updateLocalPortfolioTargetWeight(id: string, targetWeight: number): void {
  writeJson(
    PORTFOLIO_KEY,
    getLocalPortfolio().map((p) => (p.id === id ? { ...p, targetWeight } : p)),
  );
}

export function hasLocalGuestData(): boolean {
  return getLocalWatchlist().length > 0 || getLocalPortfolio().length > 0;
}

export function clearLocalGuestData(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(WATCHLIST_KEY);
  localStorage.removeItem(PORTFOLIO_KEY);
  localStorage.removeItem(WATCHLIST_GROUPS_KEY);
}

export const LOCAL_PORTFOLIO_EVENTS = [
  `${WATCHLIST_KEY}-changed`,
  `${PORTFOLIO_KEY}-changed`,
  `${WATCHLIST_GROUPS_KEY}-changed`,
];
